import { Dispatch, SetStateAction } from "react"

import { parseStream } from "@/components/CopilotAnalyze/streaming/helpers"

import type {
    CopilotInputMessageInterface,
    CopilotChatInterface,
    CopilotChatsInterface,
    ChatMessageInterface,
    SendFeedbackInterface,
} from "@/components/CopilotAnalyze/types"

interface JwtResponse {
    jwt: string
}

const DEFAULT_TIMEOUT = 5000
const MAX_RETRIES = 3
const API_ENDPOINT =
    window.ENVIRONMENT === "Development"
        ? "http://localhost:8003"
        : window.ENVIRONMENT === "Staging"
          ? "https://copilot-ai.staging.quorum.us"
          : "https://copilot-ai.quorum.us"

export const auth = {
    fetchJwt: async () => {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT)

        try {
            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                try {
                    const response = await fetch(`/copilot/generate_jwt_token/`, {
                        headers: { "Content-Type": "application/json" },
                        signal: controller.signal,
                    })

                    if (!response.ok) {
                        throw new Error("Error fetching Copilot JWT", {
                            cause: { status: response.status, body: await response.text() },
                        })
                    }

                    const data = await response.json()

                    return data as JwtResponse
                } catch (error) {
                    if (attempt === MAX_RETRIES - 1) throw error
                    await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, attempt)))
                }
            }
        } finally {
            clearTimeout(timeoutId)
        }
    },
}

export const chat = {
    /** Fetch all Chats for the current user (identified via the JWT) */
    fetchHistory: async (
        /** JWT Token for the Copilot service. Can be read from `useCopilotAuth()` */
        jwt: string,
    ) => {
        try {
            const response = await fetch(`${API_ENDPOINT}/history/`, { headers: { Authorization: `Bearer ${jwt}` } })
            const responseJson = await response.json()

            return responseJson as CopilotChatsInterface
        } catch (e) {
            console.error(e)
        }
    },

    /** Send a message to the Copilot service and process the SSE response */
    sendMessage: async ({
        sessionId,
        message,
        jwt,
        setActiveSession,
        setIsLoading,
        currentRegion,
    }: {
        sessionId: string
        message: CopilotInputMessageInterface
        jwt: string
        setActiveSession: Dispatch<SetStateAction<CopilotChatInterface>>
        setIsLoading: Dispatch<SetStateAction<boolean>>
        currentRegion: string | undefined
    }) => {
        try {
            const response = await fetch(`${API_ENDPOINT}/chat`, {
                headers: {
                    Authorization: `Bearer ${jwt}`,
                    "Content-Type": "application/json",
                },
                method: "POST",
                body: JSON.stringify({
                    session_id: sessionId,
                    message: message.content,
                    current_url: window.location.href,
                    current_region: currentRegion,
                }),
            })

            if (!response.ok) throw new Error(response.statusText)

            const stream = response.body
            if (!stream) throw new Error("No data received")

            // The return value of the mutationFn is passed as TData to the onSuccess callback
            let newMessage: ChatMessageInterface

            for await (const partialMessage of parseStream(stream)) {
                if (partialMessage) {
                    newMessage = partialMessage

                    setActiveSession((previousChat) => {
                        if (!previousChat) return previousChat

                        const previousMessages = structuredClone(previousChat.messages)
                        const lastMessageId = Object.keys(previousMessages).pop()

                        if (lastMessageId === partialMessage.id) {
                            // Last message ID matches the new one, so we're just updating it
                            // set loading false as it is a update we don't need to show loading message
                            setIsLoading(false)
                            return {
                                ...previousChat,
                                messages: Object.assign({}, previousMessages, {
                                    [lastMessageId]: Object.assign({}, previousMessages[lastMessageId], partialMessage),
                                }),
                            }
                        } else {
                            // Last message ID doesn't match the new one, so we're creating a new message
                            return {
                                ...previousChat,
                                messages: Object.assign({}, previousMessages, {
                                    [partialMessage.id]: Object.assign({}, partialMessage),
                                }),
                            }
                        }
                    })
                }
            }

            return newMessage
        } catch (e) {
            throw e
        }
    },

    /** Provide feedback on a message */
    sendFeedback: async ({ feedback, spanId, traceId }: SendFeedbackInterface, jwt: string) => {
        if (feedback !== "Copied") {
            await fetch(`${API_ENDPOINT}/feedback`, {
                headers: {
                    Authorization: `Bearer ${jwt}`,
                    "Content-Type": "application/json",
                },
                method: "POST",
                body: JSON.stringify({
                    span_id: spanId,
                    trace_id: traceId,
                    feedback,
                }),
            })
        }
    },

    /** Rename a Chat */
    rename: async ({ id, label }: Pick<CopilotChatInterface, "id" | "label">, jwt: string) => {
        return fetch(`${API_ENDPOINT}/chat/${id}`, {
            headers: {
                Authorization: `Bearer ${jwt}`,
                "Content-Type": "application/json",
            },
            method: "PUT",
            body: JSON.stringify({ label }),
        })
    },

    /** Delete a Chat */
    delete: async (id: string, jwt: string) => {
        return fetch(`${API_ENDPOINT}/chat/${id}`, {
            headers: {
                Authorization: `Bearer ${jwt}`,
                "Content-Type": "application/json",
            },
            method: "DELETE",
        })
    },
}
